import { isDamaged } from "@redotech/redo-model/return";
import {
  AdvancedExchangeItem,
  BundleRules,
  BundleType,
  BundleValueType,
  ExpandBundleRules,
  ExpandedBundle,
  LINE_ITEM_SOURCE_INDEX,
  MergedBundle,
  ProvisionType,
  ReturnOptionMethod,
  ReturnableItem,
  ShopifyProperty,
} from "@redotech/redo-model/return-flow";
import {
  DiscountName,
  PriceOption,
  SkuOption,
} from "@redotech/redo-model/return-flow/condition";
import {
  ReturnTotals,
  ReturnTotalsCalculator,
} from "@redotech/redo-model/return-totals-calculator";
import { Channel, SalesChannels } from "@redotech/redo-model/sales-channels";
import {
  DiscountDistributionMethod,
  ExchangeGroup,
  Location,
  LocationCondition,
  Settings,
} from "@redotech/redo-model/team";
import {
  getCollectionsForProduct,
  getLocations,
  getProduct,
  getProductsBySkus,
  getSettings,
} from "../api";

export const REDO_APP_ID = "3426665";

export async function setFavicon() {
  const response = await getSettings();
  const settings = response?.data;
  // https://app.clickup.com/t/868680b85
  const url = settings?.theme.favicon_url || settings?.theme.logo_url;
  if (url) {
    const link = document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "shortcut icon";
    link.href = url;
    document.head.appendChild(link);
  }
}

export function optional<T, V>(
  value: T | null | undefined,
  fn: (value: T) => V,
): V | null {
  if (value === null || value === undefined) {
    return null;
  }

  return fn(value);
}

export const calculateReturnTotals = (
  itemsReadyForReturn,
  currentOrder,
  settings,
  shopifyStorefrontCart,
) => {
  const products = [];
  for (const item of getProductsFromReturnableItems(itemsReadyForReturn)) {
    const adjustment = item.selected_return_option?.adjustment || {
      flat: 0,
      proportion: 1,
    };
    const priceAdjustment = getAdjustmentValue(adjustment, item.discount_price);
    products.push(apiOutput(item, priceAdjustment, currentOrder, settings));
  }
  const returnTotalsCalculator = new ReturnTotalsCalculator(
    {
      provision: ProvisionType.PROCESSED,
      products,
      new_order_taxes:
        shopifyStorefrontCart?.cost?.totalTaxAmount?.amount ?? "0.00",
      totals: {
        fee: 0,
      },
      discount_allocations: shopifyStorefrontCart?.discountAllocations || [],
      advancedExchangeItems: getNewItems(shopifyStorefrontCart, []),
    },
    currentOrder,
    { settings },
  );
  const returnTotals: ReturnTotals =
    returnTotalsCalculator.getTotalsForAllProducts();

  return returnTotals;
};

export const getAdjustmentValue = (adjustment, discountPrice) => {
  const adjustmentValue =
    parseFloat(discountPrice) * (adjustment.proportion - 1.0) + adjustment.flat;

  return adjustmentValue;
};

export const getShowAdjustment = (adjustment) => {
  return adjustment.flat !== 0 || adjustment.proportion !== 1;
};

export const getNewItems = (shopifyStorefrontCart, taxes) => {
  const newItems = [];
  const cartDiscountPercentage =
    parseFloat(
      shopifyStorefrontCart?.cost?.checkoutChargeAmount?.amount ?? "0.00",
    ) /
    parseFloat(shopifyStorefrontCart?.cost?.subtotalAmount?.amount ?? "0.00");
  if (shopifyStorefrontCart?.lines?.edges?.length > 0) {
    let priceOfItems = 0.0;
    for (const cartItem of shopifyStorefrontCart.lines.edges) {
      const graphqlVariantId = cartItem.node.merchandise.id;

      // Parse the variant id from the graphql id
      const variantId = graphqlVariantId.substring(
        graphqlVariantId.indexOf("ProductVariant/") + "ProductVariant/".length,
      );
      priceOfItems += parseFloat(cartItem.node.cost.totalAmount.amount);
      const tax = taxes.find((tax) => tax.variantId === variantId);
      const newItem: AdvancedExchangeItem = {
        variantId: variantId,
        title: cartItem.node.merchandise.product.title,
        variantTitle:
          cartItem?.node?.merchandise?.title !== "Default Title"
            ? cartItem.node.merchandise.title
            : "",
        // Storefront cart uses the total value for a line item, we will split the price by each product
        price: (
          parseFloat(cartItem.node.cost.totalAmount.amount) /
          cartItem.node.quantity
        ).toFixed(2),
        itemValue: (
          (parseFloat(cartItem.node.cost.totalAmount.amount) /
            cartItem.node.quantity) *
          cartDiscountPercentage
        ).toFixed(2),
        images: [cartItem.node.merchandise.image.originalSrc],
        quantity: 1,
        currencyCode: cartItem.node.cost.totalAmount.currencyCode,
        attributes: cartItem.node.attributes || [],
        tax: tax?.tax || "0.00",
      };
      for (let i = 0; i < cartItem.node.quantity; i++) {
        newItems.push(newItem);
      }
    }
  }

  return newItems;
};

export const apiOutput = (item, priceAdjustment, order, settings) => {
  return {
    multipleChoiceAnswers: item.multipleChoiceAnswers,
    order_id: order._id,
    type: item.green_return
      ? "green_return"
      : item.selected_return_option?.method.type ===
          ReturnOptionMethod.REFUND ||
        item.selected_return_option?.method.type === ReturnOptionMethod.CREDIT
      ? "return"
      : "exchange",
    strategy: item.green_return
      ? "green_return"
      : item.selected_return_option?.method.type === ReturnOptionMethod.EXCHANGE
      ? settings.exchanges.excessExchangeValue
      : item.selected_return_option?.method.type === ReturnOptionMethod.REFUND
      ? "refund"
      : "store_credit",
    line_item_id: item.id,
    quantity: item.quantity,
    tags: item.tags,
    returnReason: item.returnReason,
    price_adjustment: priceAdjustment,
    productId: item.product_id,
    price: item.discount_price,
    sku: item.sku,
    new_item: item.new_item,
    exchangeGroupItem: item.exchangeGroupItem,
    isUnbundled: item.isUnbundled,
  };
};

export const extractPriceFromProperties = (lineItem, order) => {
  let originItemPrice;
  let originItemDiscountPrice;
  const originItem =
    order?.originItems?.find(
      (item) =>
        item.applicableShopifyOrder === order.shopify.id &&
        item.newVariantId === lineItem.variant_id,
    ) ||
    order?.originItems?.find(
      (item) => item.newVariantId === lineItem.variant_id,
    );
  if (originItem) {
    originItemPrice = originItem.preDiscountPrice || originItem.unitPrice;
    originItemDiscountPrice = originItem.unitPrice;
  }
  if (!originItemPrice) {
    const originItem = lineItem.properties?.find(
      ({ name }) => name === "_redo_origin_item",
    );
    originItemPrice =
      originItem?.preDiscountPrice || originItem?.unitPrice || null;
    originItemDiscountPrice = originItem?.unitPrice || null;
  }

  return originItemPrice
    ? {
        original_price: originItemPrice,
        discount_price: originItemDiscountPrice,
      }
    : null;
};

export const hasRedoDiscountCode = (data) => {
  return data.shopify.discount_applications?.some(
    (discount) => discount.code?.includes("REDO"),
  );
};

export const getItemData = (
  item,
  distributionMethod,
  order,
  product = undefined,
) => {
  const extractedPrice = extractPriceFromProperties(item, order);
  // Won't make api request if it already has for that product.
  // let product = products.find((product) => product.id === item.product_id);

  return {
    ...item,
    ...{
      processed_at: new Date(order.shopify.processed_at),
      shipping_fees: {
        store_credit: 0,
        exchange: 0,
        refund: 0,
      },
      coverage: {
        exchange: order.protected && order.coverage.exchange,
        refund: order.protected && order.coverage.refund,
        store_credit: order.protected && order.coverage.storeCredit,
      },
      original_price: item.price,
      discount_price: ReturnTotalsCalculator.getDiscountPrice(
        item,
        order,
        distributionMethod,
      ),
      green_return_selected: false,
      green_return_type: "discount", //Default value
      return: null, // This is to define what we do with the product.
      product: product,
      fixed_strategies: [],
      is_exchange_order: order.isExchangeOrder,
      is_defective: null,
      is_wrong_item: null,
      needs_manual_review: false,
      // We use this flag to determine if we should split this single item in a new
      // return. This isu used for returns that require approval by the merchant.
      split: false,
      inputAnswers: [], // should be type InputAnswer
      readyToReturn: false, // Set this to true once user has completed flow for item.
      selected_return_option: null,
      new_item: null,
      returnReason: undefined,
      initialLineItem: undefined,
      ...extractedPrice,
    },
  };
};

export const getDistributionMethod = async (settings: any) => {
  return (
    settings.discountDistributionMethod || DiscountDistributionMethod.LINE_ITEM
  );
};

export const makeOrder = async (data, settings) => {
  const tempData = data;
  const lineItems = data.shopify.line_items.filter(
    (item) => !ReturnTotalsCalculator.isRedoItem(item),
  );
  const productIds = [];
  lineItems.forEach((item) => {
    if (!productIds.includes(item.product_id)) {
      productIds.push(item.product_id);
    }
  });

  const products = [];
  await Promise.all(
    productIds.map(async (productId) => {
      products.push((await getProduct(productId)).data);
    }),
  );

  const distributionMethod = await getDistributionMethod(settings);

  const modifiedLineItems = lineItems.map((item) => {
    // const extractedPrice = extractPriceFromProperties(item, data);
    // Won't make api request if it already has for that product.
    const product = products.find((product) => product.id === item.product_id);

    return getItemData(item, distributionMethod, data, product);
  });
  tempData.line_items = modifiedLineItems;

  if (settings?.exchanges?.blockDiscountsOnExchanges && tempData.shopify) {
    tempData.shopify.discount_codes = [];
  }
  return tempData;
};

const transformValue = (parsedValue: string, bundleRules: BundleRules) => {
  let transformedValue = parsedValue;
  for (const valueTransform of bundleRules.valueTransforms) {
    transformedValue = transformedValue.replace(
      new RegExp(valueTransform.search, "g"),
      valueTransform.replace,
    );
  }

  return transformedValue;
};

const getQuantity = (parsedValue: string, bundleRules: BundleRules) => {
  const quantity = parsedValue.match(new RegExp(bundleRules.quantityParser));
  if (!quantity) {
    return 1;
  }
  return parseInt(quantity[1]);
};

const parseValue = (value: string, bundleRules: BundleRules) => {
  const parsedValues: string[] = value.match(
    new RegExp(bundleRules.valueParser, "g"),
  );
  const valuesWithQuantity = parsedValues.flatMap((parsedValue) => {
    const quantity: number = getQuantity(parsedValue, bundleRules);
    return Array<string>(quantity).fill(parsedValue);
  });
  const transformedValues = valuesWithQuantity.map((value) =>
    transformValue(value, bundleRules),
  );

  return transformedValues.join(bundleRules.valueDelimiter);
};

export const parseGid = (gid: string) => {
  return /[^/]*$/.exec(gid)![0];
};

const unbundleItems = (item: any, bundle: ExpandedBundle) => {
  const bundles: MergedBundle[] = [];
  for (const part of bundle.items) {
    part.returnReason = item.returnReason;
    bundles.push({
      id: 0, // We will set the id later when we add it to the returnableItems array
      items: [
        {
          ...part,
          isUnbundled: true,
          allowFutureReturns: true,
        },
      ],
      lineItemSource: item,
      isExpanded: false,
      rules: bundle.rules,
      value: bundle.value,
      lineItemSourceIndex: LINE_ITEM_SOURCE_INDEX,
    });
  }

  return bundles;
};

// We are taking one line item and splitting it into multiple returnable items
const createExpandedBundle = async (
  item: any,
  matchedProperty: ShopifyProperty,
  bundleRules: BundleRules,
  order: any,
  settings: any,
): Promise<ExpandedBundle> => {
  const items = [];
  if (bundleRules.valueType === BundleValueType.SKU) {
    const parsedValue = parseValue(matchedProperty.value, bundleRules);
    const parsedValueList = parsedValue.split(bundleRules.valueDelimiter);
    const response = await getProductsBySkus(parsedValueList);
    const distributionMethod = await getDistributionMethod(settings);

    for (const product of response.data) {
      const part = {
        variant_id: parseInt(product.legacyResourceId),
        id: item.id,
        variant_title: product.title,
        sku: product.sku,
        price: product.price,
        image: {
          src: product.image?.url || product.product.featuredImage?.url,
        },
        admin_graphql_api_id: product.product.id,
        title: product.product.title,
        discount_allocations: [],
        fulfillment_status: item.fulfillment_status,
        product_id: parseGid(product.product.id),
        tags: item.tags,
      };
      items.push(getItemData(part, distributionMethod, order));
    }
  }

  return {
    id: item.id,
    rules: bundleRules,
    value: matchedProperty.value,
    items,
    lineItemSource: item,
    bundledItem: item,
    isExpanded: true,
    returningParts: false,
    partsToReturn: [],
  };
};

const containsBundleKey = async (
  item,
  bundleRules: BundleRules,
): Promise<ShopifyProperty> => {
  if (bundleRules.isProperty) {
    const matchedProperty: ShopifyProperty = item.properties.find(
      (property: { name: string }) => property.name === bundleRules.key,
    );
    if (matchedProperty) {
      return matchedProperty;
    }
  } else {
    switch (bundleRules.valueType) {
      case BundleValueType.SKU:
        if (item.sku.includes(bundleRules.key)) {
          return {
            name: bundleRules.key,
            value: item.sku,
          };
        }
      // TODO: potentially implement other types here?
    }
  }

  return null;
};

const containsBundleValue = (item, value: string, bundleRules: BundleRules) => {
  let containsBundleValue = false;
  const parsedValue = parseValue(value, bundleRules);
  const parsedValueList = parsedValue.split(bundleRules.valueDelimiter);
  for (const value of parsedValueList) {
    switch (bundleRules.valueType) {
      case BundleValueType.SKU:
        containsBundleValue = item.sku === value;
        break;
      case BundleValueType.VARIANT_ID:
        containsBundleValue = item.variant_id == value;
        break;
      case BundleValueType.BUNDLE_ID:
        containsBundleValue =
          item.properties.find(
            (property: ShopifyProperty) => property.name === bundleRules.key,
          )?.value === value;
        break;
    }
    if (containsBundleValue) {
      break;
    }
  }

  return containsBundleValue;
};

const getReturnableQuantity = (order, lineItem) => {
  const numReturned = lineItem.returns.filter(
    (return_) => !return_.isUnbundled,
  ).length;

  lineItem.numRefunded = 0;
  for (const refund of order.shopify.refunds) {
    if (
      // If there are no transactions don't count it as a refund
      // (e.g. if we issue a $0 refund there are no transactions)
      !refund.transactions?.length ||
      refund.transactions.some(
        (transaction: { source_name: string }) =>
          transaction.source_name === REDO_APP_ID,
      )
    ) {
      // The refund was issued by us, so don't count it (we already
      // accounted for it in the numReturned variable above)
      continue;
    }
    for (const refunded_item of refund.refund_line_items) {
      if (refunded_item.line_item_id === lineItem.id) {
        lineItem.numRefunded += refunded_item.quantity;
      }
    }
  }

  const returnableQuantity =
    lineItem.quantity - numReturned - lineItem.numRefunded;

  return returnableQuantity > 0 ? returnableQuantity : 0;
};

// e.g. [{ item: "a", quantity: 2 }] => [{ item: "a", quantity: 1 }, { item: "a", quantity: 1 }]
const separateByQuantity = (order, lineItems) => {
  return lineItems.reduce((items, item) => {
    return [
      ...items,
      ...Array(getReturnableQuantity(order, item)).fill({
        ...item,
        quantity: 1,
        originalQuantity: item.quantity,
      }),
    ];
  }, []);
};

const getBundleMaxQuantity = (bundle: ReturnableItem) => {
  switch (true) {
    case !bundle.rules:
      return 1;
    case bundle.rules.valueType === BundleValueType.BUNDLE_ID:
      return Infinity;
    default:
      return 2;
  }
};

const canAddToBundle = (item, bundle: ReturnableItem) => {
  if (!bundle.rules || bundle.items.length >= getBundleMaxQuantity(bundle)) {
    return false;
  }

  return containsBundleValue(item, bundle.value, bundle.rules);
};

const addToExistingBundle = (item, bundles: ReturnableItem[]) => {
  for (const bundle of bundles) {
    if (!(bundle.rules as ExpandBundleRules)?.variantExchangeOnly) {
      continue;
    }
    if (canAddToBundle(item, bundle)) {
      bundle.items.push(item);
      setLineItemSource(bundle);
      return true;
    }
  }

  return false;
};

export const setLineItemSource = (returnableItem: ReturnableItem) => {
  if (returnableItem.isExpanded) {
    returnableItem.lineItemSource = returnableItem.bundledItem;
  } else if (returnableItem.isExpanded === false) {
    returnableItem.lineItemSource =
      returnableItem.items[returnableItem.lineItemSourceIndex];
    const combinedProductTags = new Set();
    for (const item of returnableItem.items) {
      item.tags.forEach((tag: string) => combinedProductTags.add(tag));
    }
    returnableItem.lineItemSource.tags = [...combinedProductTags];
  }
};

export const getReturnableItems = async (
  order,
  settings,
  bundleRulesList: BundleRules[],
) => {
  try {
    const returnableItems: ReturnableItem[] = [];
    const unmatchedItems = [];
    const items = separateByQuantity(order, order.line_items);
    for (const lineItem of items) {
      let matchedRules: BundleRules, matchedProperty: ShopifyProperty | null;
      for (const bundleRules of bundleRulesList) {
        matchedProperty = await containsBundleKey(lineItem, bundleRules);
        if (matchedProperty) {
          matchedRules = bundleRules;
          break;
        }
      }
      if (matchedRules) {
        if (matchedRules.type === BundleType.EXPAND) {
          const bundle = await createExpandedBundle(
            lineItem,
            matchedProperty,
            matchedRules,
            order,
            settings,
          );
          if (!(matchedRules as ExpandBundleRules).variantExchangeOnly) {
            unbundleItems(lineItem, bundle).forEach((itemInBundle) => {
              const quantityReturned = lineItem.returns.filter(
                (return_) =>
                  return_.variant_id == itemInBundle.items[0].variant_id,
              ).length;
              const quantityInBundle =
                bundle.items.filter(
                  (item) => item.variant_id == itemInBundle.items[0].variant_id,
                ).length * lineItem.originalQuantity;
              const quantityAdded = returnableItems.filter(
                (returnableItem) =>
                  returnableItem.items[0].variant_id ==
                  itemInBundle.items[0].variant_id,
              ).length;
              if (quantityInBundle > quantityReturned + quantityAdded) {
                itemInBundle.items[0].discount_price =
                  ReturnTotalsCalculator.getDiscountPrice(
                    lineItem,
                    order,
                    undefined,
                    itemInBundle.items[0],
                  );
                returnableItems.push({
                  ...itemInBundle,
                  id: returnableItems.length,
                });
              }
            });
          } else {
            returnableItems.push(bundle);
          }
        } else {
          const addedToBundle = addToExistingBundle(lineItem, returnableItems);
          if (!addedToBundle) {
            const bundle: MergedBundle = {
              id: returnableItems.length,
              rules: matchedRules,
              value: matchedProperty.value,
              items: [lineItem],
              lineItemSource: lineItem,
              lineItemSourceIndex: LINE_ITEM_SOURCE_INDEX,
              isExpanded: false,
            };
            returnableItems.push(bundle);
          }
        }
      } else {
        unmatchedItems.push(lineItem);
      }
    }

    for (const item of unmatchedItems) {
      const addedToBundle = addToExistingBundle(item, returnableItems);
      if (!addedToBundle) {
        const bundle: MergedBundle = {
          id: returnableItems.length,
          items: [item],
          lineItemSource: item,
          lineItemSourceIndex: LINE_ITEM_SOURCE_INDEX,
          isExpanded: false,
        };
        returnableItems.push(bundle);
      }
    }

    return returnableItems;
  } catch (e) {
    console.error(e);
  }
};

export const setFieldOnItemBeingReturned = <T>(
  returnableItem: ReturnableItem,
  field: string,
  value: T,
) => {
  for (const item of returnableItem.items) {
    item[field] = value;
  }
  if (returnableItem.isExpanded) {
    for (const part of returnableItem.partsToReturn) {
      part[field] = value;
    }
    returnableItem.bundledItem[field] = value;
  }
};

export const getBundleTotalPriceBeforeDiscount = (bundle: ReturnableItem) => {
  if (bundle.isExpanded) {
    return parseFloat(bundle.bundledItem.price);
  } else {
    return bundle.items.reduce(
      (acc: number, item: any) => acc + parseFloat(item.price),
      0,
    );
  }
};

export const getBundleTotalDiscountPrice = (bundle: ReturnableItem) => {
  if (bundle.isExpanded) {
    return parseFloat(bundle.bundledItem.discount_price);
  } else {
    return bundle.items.reduce(
      (acc: number, item: any) => acc + parseFloat(item.discount_price),
      0,
    );
  }
};

export const getProductsFromReturnableItems = (
  returnableItems: ReturnableItem[],
) => {
  const items = [];
  for (const returnableItem of returnableItems) {
    if (!returnableItem.isExpanded) {
      returnableItem.items.forEach((item) => items.push(item));
    } else if (returnableItem.returningParts) {
      returnableItem.partsToReturn.forEach((part) => items.push(part));
    } else {
      items.push(returnableItem.bundledItem);
    }
  }

  return items;
};

export const exchangeGroupFilter = (
  product,
  exchangeGroup: ExchangeGroup,
  collectionProducts = null,
) => {
  if (!exchangeGroup) {
    return true;
  }
  switch (exchangeGroup.type) {
    case "tag:tag":
      return product.tags.some((tag: string) =>
        exchangeGroup.targetTags.includes(tag),
      );
    case "tag:collection":
    case "name:collection":
      return collectionProducts?.some(
        (collectionProduct) => collectionProduct.id === product.id,
      );
  }
};

export const isVariantExchange = (item: {
  new_item?: any;
  exchangeGroupItem?: any;
}) => {
  return item.new_item || item.exchangeGroupItem;
};

async function getFulfilledFromAddress(locationId, shopifyLocations) {
  const response = shopifyLocations || (await getLocations());

  const location = response.data.locations.find(
    (location) => location.id === locationId,
  );
  if (location) {
    return {
      city: location.city,
      country: location.country,
      country_name: location.country_name,
      name: location.name,
      phone: location.phone,
      state: location.province_code,
      street1: location.address1,
      street2: location.address2,
      zip: location.zip,
    };
  } else {
    // No original location
    return null;
  }
}

async function getAddress(address, originalAddress, originalLocation) {
  if (originalLocation && originalAddress) {
    return originalAddress;
  }
  return address;
}

type MultipleChoiceAnswer = {
  answer: string;
  questionText: string;
};
export const getMerchantAddress = async (
  locationSettings: Location[],
  productTags: string[],
  returnReason: string,
  fulfilledFromLocationId: any,
  multipleChoiceAnswers: MultipleChoiceAnswer[],
  returnType: "return" | "claim",
  productId: string,
  orderTags: string[],
  customerTags: string[],
  discounts: DiscountName[],
  price: string,
  sku: string,
  salesChannel: string,
  customerCountry: string,
  shopifyLocations = null,
) => {
  const fulfilledFromLocation = await getFulfilledFromAddress(
    fulfilledFromLocationId,
    shopifyLocations,
  );
  let collections = [];
  for (const location of locationSettings) {
    if (location.condition == LocationCondition.COLLECTIONS) {
      const response = await getCollectionsForProduct(productId);
      collections = response.data || [];
    }
    if (!location.flowTypes.includes(returnType)) {
      continue;
    }
    switch (location.condition) {
      case LocationCondition.PRODUCT_TAGS: {
        const locationTags = location.tags.map((tag: string) =>
          tag.toLowerCase(),
        );
        const matchingTags = productTags.filter((tag: string) =>
          locationTags.includes(tag.toLowerCase()),
        );
        if (matchingTags.length > 0) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      }
      case LocationCondition.MULTIPLE_CHOICE: {
        const matchingQuestion = multipleChoiceAnswers.find(
          (answer) => answer.questionText === location.multipleChoiceQuestion,
        );
        if (
          matchingQuestion &&
          location.multipleChoiceQuestionAnswers.includes(
            matchingQuestion.answer,
          )
        ) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      }
      case LocationCondition.RETURN_REASON:
        if (location.returnReasons.includes(returnReason)) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      case LocationCondition.DAMAGED:
        if (isDamaged(returnReason)) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      case LocationCondition.NAME:
        if (fulfilledFromLocation.name === location.address.name) {
          return location.address;
        }
        break;
      case LocationCondition.COLLECTIONS:
        if (
          location.collections.some((collection) =>
            collections.includes(collection),
          )
        ) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      case LocationCondition.ORDER_TAGS: {
        const locationOrderTags = location.tags.map((tag: string) =>
          tag.toLowerCase(),
        );
        const orderMatchingTags = orderTags.filter((tag: string) =>
          locationOrderTags.includes(tag.toLowerCase()),
        );
        if (orderMatchingTags.length > 0) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      }
      case LocationCondition.CUSTOMER_TAGS: {
        const locationCustomerTags = location.tags.map((tag: string) =>
          tag.toLowerCase(),
        );
        const customerMatchingTags = customerTags.filter((tag: string) =>
          locationCustomerTags.includes(tag.toLowerCase()),
        );
        if (customerMatchingTags.length > 0) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      }
      case LocationCondition.DISCOUNTS: {
        const names = new Set(
          location.discounts.map((name) => name.toLowerCase()),
        );
        const foundMatchingDiscount = discounts.some(
          (discount) =>
            names.has(discount.code.toLowerCase()) ||
            names.has(discount.title.toLowerCase()),
        );
        if (foundMatchingDiscount) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      }
      case LocationCondition.PRICE: {
        let matchesCondition = false;
        switch (location.priceMatchType) {
          case PriceOption.EQUALS:
            matchesCondition = price === location.price;
            break;
          case PriceOption.LESS_THAN:
            matchesCondition = parseFloat(price) < parseFloat(location.price);
            break;
          case PriceOption.GREATER_THAN:
            matchesCondition = parseFloat(price) > parseFloat(location.price);
            break;
          case PriceOption.CENTS_EQUALS:
            // Just get the cents values from each.
            matchesCondition =
              parseFloat(price).toFixed(2).slice(-2) ===
              parseFloat(location.price).toFixed(2).slice(-2);
            break;
        }
        if (matchesCondition) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      }
      case LocationCondition.SKU: {
        let matchesSku = false;
        switch (location.skuMatchType) {
          case SkuOption.EQUALS:
            matchesSku = location.skus.includes(sku.toLowerCase());
            break;
          case SkuOption.STARTS_WITH:
            matchesSku = location.skus.some((code) => {
              return sku.toLowerCase().startsWith(code);
            });
            break;
          case SkuOption.ENDS_WITH:
            matchesSku = location.skus.some((code) => {
              return sku.toLowerCase().endsWith(code);
            });
            break;
          case SkuOption.INCLUDES:
            matchesSku = location.skus.some((code) => {
              return sku.toLowerCase().includes(code);
            });
            break;
        }
        if (matchesSku) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      }
      case LocationCondition.SALES_CHANNEL: {
        let found = false;
        SalesChannels.forEach((channel: Channel) => {
          if (location.salesChannels.includes(channel.id)) {
            channel.sourceNames.forEach((sourceName: string) => {
              if (salesChannel.startsWith(sourceName)) {
                found = true;
              }
            });
          }
        });
        if (found) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      }
      case LocationCondition.CUSTOMER_COUNTRY:
        if (location.countries.includes(customerCountry)) {
          return getAddress(
            location.address,
            fulfilledFromLocation,
            location.originalLocation,
          );
        }
        break;
      default:
        return getAddress(
          location.address,
          fulfilledFromLocation,
          location.originalLocation,
        );
    }
  }
  // if we didn't match any of the locations, return the default location (the last one)
  const defaultLocation = locationSettings[locationSettings.length - 1];
  return getAddress(
    defaultLocation.address,
    fulfilledFromLocation,
    defaultLocation.originalLocation,
  );
};

/**
 * Example usage:
 *  array: ["a", "b", "c"], conjunction: "or" => "a, b, or c"
 *  array: ["a", "b"], conjunction: "and" => "a and b"
 *  array: ["a"], conjunction: "and" => "a"
 */
export const arrayToString = (array: string[], conjunction: string): string => {
  switch (array.length) {
    case 0:
      return "";
    case 1:
      return array[0];
    case 2:
      return `${array[0]} ${conjunction} ${array[1]}`;
    default: {
      let output = array.slice(0, array.length - 1).join(", ");
      output += `, ${conjunction} ${array[array.length - 1]}`;
      return output;
    }
  }
};

const emailRegex = /\S+@\S+\.\S+/;
export const isValidEmail = (email: string): boolean => emailRegex.test(email);

//// Commented out because this wasn't being used and evaluate call is out of date ////
// export const preprocessFreStatus = async (
//   currentOrder: any,
//   product: any,
//   returnFlow: ReturnFlow,
//   settings: any,
// ): Promise<String | null> => {
//   let currentStep = returnFlow.steps[product.reset?.step || 0];
//   const fulfillment = currentOrder.lineItemsFulfillment.find(
//     (item) => item.shopify_line_item_id === product.id.toString(),
//   );
//   const fulfilledAt = fulfillment?.fulfilled_at
//     ? Temporal.Instant.from(fulfillment.fulfilled_at)
//     : null;
//   const deliveredAt = fulfillment?.delivered_at
//     ? Temporal.Instant.from(fulfillment.delivered_at)
//     : // If there is no delivered_at date, we assume it was delivered 10 days after it was fulfilled.
//       instantAddUtc(fulfilledAt, Temporal.Duration.from({ days: 10 }));
//   const orderedAt = Temporal.Instant.from(currentOrder.shopifyCreatedAt);
//
// while (currentStep.type === Step.CONDITION) {
//   let collections = [];
//   if (currentStep.condition.type == Condition.COLLECTION) {
//     const response = await getCollectionsForProduct(product.product_id);
//     collections = response.data || [];
//   }
//   if (
//     Condition.evaluate(currentStep.condition, {
//       coverage: currentOrder.protected
//         ? Coverage.COVERED
//         : Coverage.NOT_COVERED,
//       discounts: currentOrder.shopify.discount_applications.map(
//         (discount) => ({
//           code: discount.code || "",
//           title: discount.title || "",
//         }),
//       ),
//       now: Temporal.Now.instant(),
//       fulfilledOn: fulfilledAt,
//       deliveredOn: deliveredAt,
//       orderedOn: orderedAt,
//       exchangeCount: currentOrder.exchangeCount,
//       sku: product.sku,
//       customerTags: currentOrder.shopify?.customer?.tags?.split(/, ?/) || [],
//       customerCountry: currentOrder.shopify?.shipping_address?.country_code,
//       orderTags: currentOrder.shopify?.tags?.split(/, ?/) || [],
//       price: parseFloat(product.discount_price),
//       productTags: product.tags,
//       collections,
//       salesChannel: currentOrder.shopify.source_name,
//       // Tells us which codes were applied to the item
//       discountCodeIndexes: product.discount_allocations.map(
//         (item: { discount_application_index: number }) =>
//           item.discount_application_index,
//       ),
//       numCustomerClaims: currentOrder.num_claims,
//       numCustomerReturns: currentOrder.num_returns,
//       numOrders: currentOrder.shopify?.customer?.orders_count,
//       method: currentOrder.shopify?.payment_gateway_names,
//       numItemsBeingReturned: 1,
//       adjustmentAmount: 0,
//       returnMethod: currentOrder.returnMethod,
//     })
//   ) {
//     currentStep = returnFlow.steps[currentStep.nextTrue];
//   } else {
//     currentStep = returnFlow.steps[currentStep.nextFalse];
//   }
// }
//
//   return currentStep.type === Step.REJECT ? currentStep.message : null;
// };

export const redirectToWebsite = (
  settings: Settings,
  window: React.MutableRefObject<Window>,
  selectedProduct?: {
    url: string;
  },
) => {
  let path = "/apps/redo/exchange-select";
  // If they have a custom storefront URL, make sure we send them there.
  if (settings.exchanges.shopSiteURL) {
    path += `?productUrl=${settings.exchanges.shopSiteURL}`;
  } else if (selectedProduct?.url) {
    path += `?productUrl=${selectedProduct.url}`;
  }
  // must happen synchronously to avoid popup blockers
  const w = globalThis.open(
    new URL(
      path,
      `https://${settings.exchanges.shopSiteDomain || settings.storeUrl}`,
    ),
  );
  if (w) {
    w.onunload = () => {
      if (w.closed) {
        window.current = undefined;
      }
    };
    // save for use by advanced-exchange
    window.current = w;
  }
};

export const redirectToWebsiteWithDiscountCode = ({
  discountCode,
  utmSource,
  utmMedium,
  settings,
  window,
  selectedProduct,
}: {
  discountCode: string;
  utmSource?: string;
  utmMedium?: string;
  settings: Settings;
  window: React.MutableRefObject<Window>;
  selectedProduct?: {
    url: string;
  };
}) => {
  const url = new URL(
    `https://${
      settings.exchanges.shopSiteDomain || settings.storeUrl
    }/apps/redo/discount-code`,
  );
  // If they have a custom storefront URL, make sure we send them there.
  if (settings.exchanges.shopSiteURL) {
    url.searchParams.set("productUrl", settings.exchanges.shopSiteURL);
  } else if (selectedProduct?.url) {
    url.searchParams.set("productUrl", selectedProduct.url);
  }

  if (utmSource) {
    url.searchParams.set("utm_source", utmSource);
  }
  if (utmMedium) {
    url.searchParams.set("utm_medium", utmMedium);
  }
  if (discountCode) {
    url.searchParams.set("discountCode", discountCode);
  }

  // must happen synchronously to avoid popup blockers
  const w = globalThis.open(url);
  if (w) {
    w.onunload = () => {
      if (w.closed) {
        window.current = undefined;
      }
    };
    // save for use by advanced-exchange
    window.current = w;
  }
};
